import { useEffect, useMemo, useState } from 'react';
import { getArtists } from '../api';
import { BOARD_FETCH_LIMIT } from '../hooks/useBoardData';
import { collectDateTimes, buildNguageByArtist, mergeArtistHeadings } from '../artistsTable/boardModel';
import { cosignMotion } from './fanModel';
import useArtistPhotos from './useArtistPhotos';

// Co-signs the board has no photo for (graduated / veteran artists are off it)
// are looked up one by one through /hub-extras - capped, since a fan can have
// dozens of co-signs and the rest fall back to initials.
const PHOTO_LOOKUP_MAX = 12;

// artistKey -> { motion, sincePct, fromMotion, photoURL } for a fan's
// co-signs, from the board (the same payload and numbers as the leaderboard
// and the homepage). Only fetched once there are co-signs to look up;
// getArtists caches it, and useCalledItEarly on the same pages asks for the
// same one.
export default function useCosignMotion(bookmarks) {
  const [json, setJson] = useState(null);
  const count = (bookmarks || []).length;

  useEffect(() => {
    if (count === 0) return undefined;
    let cancelled = false;
    getArtists(BOARD_FETCH_LIMIT)
      .then((j) => { if (!cancelled) setJson(j || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [count]);

  const fromBoard = useMemo(() => {
    if (!json || count === 0) return {};
    const data = json.daily_update || {};
    const artistsCollection = json.artists_collection || {};
    return cosignMotion(bookmarks, {
      artistNames: mergeArtistHeadings(data, artistsCollection),
      allDateTimes: collectDateTimes(data),
      nguageByArtist: buildNguageByArtist(json.nguage_history || {}),
      artistsCollection,
    });
  }, [json, bookmarks, count]);

  const missingPhotos = json
    ? (bookmarks || []).map((b) => b?.artistKey).filter((k) => k && !fromBoard[k]?.photoURL).slice(0, PHOTO_LOOKUP_MAX)
    : [];
  const extraPhotos = useArtistPhotos(missingPhotos);

  return useMemo(() => {
    const keys = Object.keys(extraPhotos);
    if (!keys.length) return fromBoard;
    const out = { ...fromBoard };
    for (const k of keys) out[k] = { ...out[k], photoURL: extraPhotos[k] };
    return out;
  }, [fromBoard, extraPhotos]);
}
